import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Handshake, UserCircle, Building2, Plus, Phone, CalendarPlus, Clock } from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandShortcut, CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { useCrmData } from "@/contexts/CrmDataContext";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const recentRecords = [
  { id: "l1", type: "Lead", name: "ERP Implementation", subtitle: "TechVista Solutions", path: "/leads" },
  { id: "d3", type: "Deal", name: "Metro Infra Deal", subtitle: "Negotiation", path: "/deals" },
  { id: "ct1", type: "Contact", name: "Vikram Mehta", subtitle: "TechVista Solutions", path: "/contacts/ct1" },
  { id: "d5", type: "Deal", name: "SafeGuard Platform", subtitle: "Negotiation", path: "/deals" },
  { id: "ct3", type: "Contact", name: "Rajesh Nair", subtitle: "GreenLeaf Organics", path: "/contacts/ct3" },
];

const typeIcon = (type: string) => {
  switch (type) {
    case "Lead": return <Target className="h-3.5 w-3.5" />;
    case "Deal": return <Handshake className="h-3.5 w-3.5" />;
    case "Contact": return <UserCircle className="h-3.5 w-3.5" />;
    case "Company": return <Building2 className="h-3.5 w-3.5" />;
    default: return null;
  }
};

const typeBadgeClass = (type: string) => {
  switch (type) {
    case "Lead": return "bg-orange-500/15 text-orange-600 border-orange-500/20";
    case "Deal": return "bg-blue-500/15 text-blue-600 border-blue-500/20";
    case "Contact": return "bg-emerald-500/15 text-emerald-600 border-emerald-500/20";
    case "Company": return "bg-purple-500/15 text-purple-600 border-purple-500/20";
    default: return "";
  }
};

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { leads, deals, contacts, customers } = useCrmData();

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleNavigate = useCallback((path: string) => {
    close();
    navigate(path);
  }, [close, navigate]);

  const handleQuickAction = useCallback((action: string) => {
    close();
    toast({ title: action, description: "This action will open the relevant form." });
    switch (action) {
      case "New Lead": navigate("/leads"); break;
      case "New Deal": navigate("/deals"); break;
      case "New Contact": navigate("/contacts"); break;
      default: break;
    }
  }, [close, navigate]);

  const allResults = useMemo(() => [
    ...leads.map(l => ({ id: l.id, type: "Lead" as const, name: l.companyName, subtitle: l.source, path: `/leads/${l.id}` })),
    ...deals.map(d => ({ id: d.id, type: "Deal" as const, name: d.name, subtitle: `${d.stage} · ${d.companyName}`, path: `/deals/${d.id}` })),
    ...contacts.map(c => ({ id: c.id, type: "Contact" as const, name: c.name, subtitle: c.customerName, path: `/contacts/${c.id}` })),
    ...customers.map(c => ({ id: c.id, type: "Company" as const, name: c.name, subtitle: c.industry, path: `/customers/${c.id}` })),
  ], [leads, deals, contacts, customers]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search leads, deals, contacts, companies…" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Recent">
          {recentRecords.map(r => (
            <CommandItem key={r.id} onSelect={() => handleNavigate(r.path)} className="gap-3">
              <Badge variant="outline" className={`${typeBadgeClass(r.type)} text-[10px] px-1.5 py-0 gap-1 font-medium`}>
                {typeIcon(r.type)} {r.type}
              </Badge>
              <span className="flex-1 text-[13px]">{r.name}</span>
              <span className="text-[11px] text-muted-foreground">{r.subtitle}</span>
              <Clock className="h-3 w-3 text-muted-foreground/50" />
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Quick Actions">
          <CommandItem onSelect={() => handleQuickAction("New Lead")} className="gap-3">
            <Plus className="h-3.5 w-3.5 text-orange-500" />
            <span className="flex-1 text-[13px]">New Lead</span>
            <CommandShortcut>⌘L</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => handleQuickAction("New Deal")} className="gap-3">
            <Plus className="h-3.5 w-3.5 text-blue-500" />
            <span className="flex-1 text-[13px]">New Deal</span>
            <CommandShortcut>⌘D</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => handleQuickAction("New Contact")} className="gap-3">
            <Plus className="h-3.5 w-3.5 text-emerald-500" />
            <span className="flex-1 text-[13px]">New Contact</span>
            <CommandShortcut>⌘J</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => handleQuickAction("Log Call")} className="gap-3">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1 text-[13px]">Log Call</span>
            <CommandShortcut>⌘⇧C</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => handleQuickAction("Schedule Meeting")} className="gap-3">
            <CalendarPlus className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1 text-[13px]">Schedule Meeting</span>
            <CommandShortcut>⌘⇧M</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {["Lead", "Deal", "Contact", "Company"].map(type => {
          const items = allResults.filter(r => r.type === type);
          if (items.length === 0) return null;
          return (
            <CommandGroup key={type} heading={`${type}s`}>
              {items.map(r => (
                <CommandItem key={r.id} onSelect={() => handleNavigate(r.path)} className="gap-3">
                  <Badge variant="outline" className={`${typeBadgeClass(r.type)} text-[10px] px-1.5 py-0 gap-1 font-medium`}>
                    {typeIcon(r.type)} {r.type}
                  </Badge>
                  <span className="flex-1 text-[13px]">{r.name}</span>
                  <span className="text-[11px] text-muted-foreground">{r.subtitle}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}
